import classNames from 'classnames';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';

import InputErrorMessage from '@basic/InputErrorMessage';
import { setupForDark } from '@theme';

type DisplayMode = 'borderless' | 'default' | 'editor';

export interface TextInputProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onError' | 'onKeyDown'
> {
  displayMode?: DisplayMode;
  value: string;
  placeholder: string;
  maxLength?: number;
  maxLengthCounterVisible?: boolean;
  maxRows?: number;
  maxLineBreaks?: number;
  autoFocus?: boolean;
  immediateChange?: boolean;
  textClassName?: string;
  blockNewLineCharacter?: boolean;
  onChange: (value: string) => void;
  onError?: (error: string) => void;
  onFinish?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

function TextInput({
  displayMode = 'editor',
  value,
  placeholder,
  maxLength,
  maxLengthCounterVisible = true,
  maxRows = 10,
  maxLineBreaks,
  autoFocus = true,
  className = '',
  textClassName = '',
  immediateChange = false,
  blockNewLineCharacter = false,
  onChange,
  onError,
  onFinish,
  onKeyDown,
  ...props
}: TextInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(null);
  const [error, setError] = useState<string | null>(null);
  const isFocusedRef = useRef(false);
  const onErrorMessage = (error: string) => {
    if (onError) {
      onError(error);
    }
    setError(error);
  };

  const validate = (text: string): boolean => {
    if (text && maxLength && text.length > maxLength) {
      onErrorMessage(t('components.textInput.maxLengthExceeded', { maxLength }));
      return false;
    }

    onErrorMessage(null);
    return true;
  };

  // Only sync the external value into local state when the field is not focused.
  // Otherwise async parent updates (debounced saves, backend re-fetches) race with
  // the user's keystrokes and clobber characters typed since the last sync.
  useEffect(() => {
    if (inputValue === null || !isFocusedRef.current) {
      setInputValue(value || '');
    }
  }, [value]);

  const onValueChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = event.target.value;
    if (maxLineBreaks !== undefined) {
      const lines = newValue.split('\n');
      if (lines.length > maxLineBreaks + 1) {
        newValue = lines.slice(0, maxLineBreaks + 1).join('\n');
      }
    }
    validate(newValue);
    setInputValue(newValue);
  };

  // SYNC UP
  useEffect(() => {
    if (!immediateChange) {
      let timeoutId = setTimeout(() => {
        if (validate(inputValue)) {
          onChange(inputValue);
        }
      }, 200);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [inputValue]);

  const onFocus = () => {
    isFocusedRef.current = true;
  };

  const onBlur = () => {
    isFocusedRef.current = false;
    if (validate(inputValue)) {
      onChange(inputValue);
    }
    if (onFinish && inputValue) {
      onFinish(inputValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (blockNewLineCharacter && event.key === 'Enter') {
      event.preventDefault();
    }
    if (maxLineBreaks !== undefined && event.key === 'Enter') {
      const currentNewLines = (inputValue || '').split('\n').length - 1;
      if (currentNewLines >= maxLineBreaks) {
        event.preventDefault();
      }
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  if (_.isNull(inputValue)) {
    return <></>;
  }

  // enlarge maxLength by 1 to show the error message
  const extraLength = maxLength && !maxLengthCounterVisible ? 1 : 0;

  let displayClassNames = '';
  let displayOuterClassNames = '';
  if (displayMode === 'editor') {
    displayClassNames = classNames('outline-none pt-sm pb-none bg-inherit');
  } else if (displayMode === 'borderless') {
    displayClassNames = classNames('outline-none py-sm bg-inherit');
  } else {
    displayClassNames = classNames(
      setupForDark('bg-text-input-default'),
      'px-sm py-xs',
      'cursor-pointer',
      'outline-none',
      setupForDark('border-text-input-default'),
      'rounded-xs',
      'border-[1px]'
    );
    displayOuterClassNames = classNames('py-xs pl-xxs');
  }

  return (
    <div
      className={classNames(
        setupForDark('text-primary placeholder:text-tertiary'),
        'w-full',
        error && onError ? setupForDark('border-error') : '',
        error && onError ? 'border-[1px]' : '',
        displayOuterClassNames,
        className
      )}
      {...props}
    >
      <TextareaAutosize
        autoFocus={autoFocus}
        maxLength={maxLength ? maxLength + extraLength : undefined}
        maxRows={maxRows}
        value={inputValue}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onValueChange}
        onKeyDown={handleKeyDown}
        className={classNames(
          setupForDark('text-primary placeholder:text-tertiary'),
          'P3R',
          'w-full',
          displayClassNames,
          textClassName
        )}
        placeholder={placeholder}
      />
      {maxLength && maxLengthCounterVisible && (
        <div
          className={classNames(
            'P6R',
            displayMode === 'borderless' && 'pt-xs mt-xs border-t-[1px]',
            displayMode === 'editor' && 'pt-xxs mt-xxs border-t-[1px]',
            setupForDark('text-tertiary border-primary')
          )}
        >
          {inputValue.length}/{maxLength}
          {maxLineBreaks !== undefined &&
            ` (${t('components.textInput.linesUsed', {
              current: inputValue ? inputValue.split('\n').length : 0,
              max: maxLineBreaks + 1
            })})`}
        </div>
      )}
      {error && !onError && <InputErrorMessage error={error} />}
    </div>
  );
}

export default TextInput;
